import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  CheckCircle, 
  Gift, 
  Shield, 
  AlertTriangle,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  Calendar,
  FileText,
  Users,
  GraduationCap,
  Clock,
  Calculator,
  HelpCircle,
  Download,
  Award,
  Building,
  Heart,
  Info,
  Check,
  X,
  BookOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { jsPDF } from 'jspdf';
import croppedRlLogo from '@/assets/cropped-rl-logo.png';

const STORAGE_KEY = 'fafsa-helper-progress';

interface ChecklistState {
  [key: string]: boolean;
}

const FafsaHelper: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [activeTab, setActiveTab] = useState('get-started');
  const [checklist, setChecklist] = useState<ChecklistState>({});
  
  // Calculator state
  const [householdSize, setHouseholdSize] = useState('4');
  const [familyIncome, setFamilyIncome] = useState('');
  const [enrollmentStatus, setEnrollmentStatus] = useState('full-time');
  const [calculatorResult, setCalculatorResult] = useState<{ min: number; max: number } | null>(null);

  // Load saved progress from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      setCurrentStep(data.currentStep || 1);
      setChecklist(data.checklist || {});
    }
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentStep, checklist }));
  }, [currentStep, checklist]);

  const toggleChecklistItem = (key: string) => {
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const studentChecklistItems = [
    { key: 'ssn', label: 'Social Security Number' },
    { key: 'email', label: 'Email address (one you check regularly)' },
    { key: 'id', label: "Driver's license or state ID (if you have one)" },
    { key: 'alien', label: 'Alien Registration Number (if not a U.S. citizen)' },
    { key: 'tax', label: '2024 Federal Tax Return (or most recent)' },
    { key: 'w2', label: 'W-2 forms and records of income' },
    { key: 'bank', label: 'Bank statements (current balances for checking/savings)' },
    { key: 'invest', label: 'Investment records (stocks, bonds, 529 plans)' },
    { key: 'colleges', label: "List of colleges you're applying to" },
  ];

  const parentChecklistItems = [
    { key: 'parent_ssn', label: 'Social Security Number' },
    { key: 'parent_email', label: 'Email address' },
    { key: 'parent_tax', label: '2024 Federal Tax Return' },
    { key: 'parent_w2', label: 'W-2 forms and records of income' },
    { key: 'parent_bank', label: 'Bank statements (current balances)' },
    { key: 'parent_invest', label: 'Investment records' },
    { key: 'parent_support', label: 'Records of child support paid or received (if applicable)' },
  ];

  const allChecklistItems = [...studentChecklistItems, ...parentChecklistItems];
  const checkedCount = Object.values(checklist).filter(Boolean).length;
  const checklistProgress = Math.round((checkedCount / allChecklistItems.length) * 100);

  const calculatePellGrant = () => {
    if (!familyIncome) return;
    
    let baseMin = 0, baseMax = 0;
    
    switch (familyIncome) {
      case 'under20':
        baseMin = 7000; baseMax = 7395;
        break;
      case '20-30':
        baseMin = 5000; baseMax = 7000;
        break;
      case '30-40':
        baseMin = 3000; baseMax = 5000;
        break;
      case '40-50':
        baseMin = 1500; baseMax = 3000;
        break;
      case '50-60':
        baseMin = 500; baseMax = 1500;
        break;
      default:
        baseMin = 0; baseMax = 0;
    }

    // Adjust for enrollment status
    let multiplier = 1;
    switch (enrollmentStatus) {
      case 'three-quarter': multiplier = 0.75; break;
      case 'half-time': multiplier = 0.5; break;
      case 'less-than-half': multiplier = 0.25; break;
    }

    setCalculatorResult({
      min: Math.round(baseMin * multiplier),
      max: Math.round(baseMax * multiplier)
    });
  };

  const generatePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let y = 20;

    // Helper function to add a new page if needed
    const checkPageBreak = (neededHeight: number) => {
      if (y + neededHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
        return true;
      }
      return false;
    };

    // Add logo
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = croppedRlLogo;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      doc.addImage(img, 'PNG', margin, y, 40, 15);
      y += 25;
    } catch (e) {
      console.log('Could not load logo for PDF');
    }

    // Title
    doc.setFontSize(24);
    doc.setTextColor(0, 46, 98); // Primary color
    doc.text('FAFSA Helper Guide', margin, y);
    y += 10;

    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('Your step-by-step guide to unlocking FREE money for college', margin, y);
    y += 15;

    // Highlight box
    doc.setFillColor(34, 197, 94); // Green
    doc.roundedRect(margin, y, pageWidth - 2 * margin, 15, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text('$3.58 BILLION in Pell Grant money goes unclaimed every year. Don\'t leave money on the table!', margin + 5, y + 10);
    y += 25;

    // Step 1: Why Athletes MUST Fill Out FAFSA
    doc.setTextColor(0, 46, 98);
    doc.setFontSize(16);
    doc.text('Step 1: Why Athletes MUST Fill Out FAFSA', margin, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const step1Points = [
      '• Free Money on Top of Your Scholarship: Up to $7,395 extra per year!',
      '• Most Athletes Don\'t Get Full Rides: Only 2% receive athletic scholarships.',
      '• Coaches May Require It: Helps stretch scholarship budgets.',
      '• Protection If Things Change: FAFSA-based aid provides a safety net.',
      '• Scholarships Don\'t Cover Everything: FAFSA can help with additional costs.'
    ];
    step1Points.forEach(point => {
      checkPageBreak(6);
      doc.text(point, margin, y);
      y += 6;
    });
    y += 8;

    // Step 2: What You'll Need
    checkPageBreak(20);
    doc.setTextColor(0, 46, 98);
    doc.setFontSize(16);
    doc.text('Step 2: What You\'ll Need (Checklist)', margin, y);
    y += 8;

    doc.setFontSize(11);
    doc.setTextColor(0, 100, 0);
    doc.text('For the Student:', margin, y);
    y += 6;

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const studentItems = [
      '☐ Social Security Number',
      '☐ Email address (one you check regularly)',
      '☐ Driver\'s license or state ID (if you have one)',
      '☐ Alien Registration Number (if not a U.S. citizen)',
      '☐ 2024 Federal Tax Return (or most recent)',
      '☐ W-2 forms and records of income',
      '☐ Bank statements (current balances)',
      '☐ Investment records (stocks, bonds, 529 plans)',
      '☐ List of colleges you\'re applying to'
    ];
    studentItems.forEach(item => {
      checkPageBreak(5);
      doc.text(item, margin + 5, y);
      y += 5;
    });
    y += 5;

    checkPageBreak(10);
    doc.setFontSize(11);
    doc.setTextColor(0, 100, 0);
    doc.text('For Your Parent(s):', margin, y);
    y += 6;

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const parentItems = [
      '☐ Social Security Number',
      '☐ Email address',
      '☐ 2024 Federal Tax Return',
      '☐ W-2 forms and records of income',
      '☐ Bank statements (current balances)',
      '☐ Investment records',
      '☐ Records of child support paid or received (if applicable)'
    ];
    parentItems.forEach(item => {
      checkPageBreak(5);
      doc.text(item, margin + 5, y);
      y += 5;
    });
    y += 8;

    // Pro tip box
    checkPageBreak(15);
    doc.setFillColor(219, 234, 254); // Light blue
    doc.roundedRect(margin, y, pageWidth - 2 * margin, 12, 2, 2, 'F');
    doc.setTextColor(30, 64, 175);
    doc.setFontSize(9);
    doc.text('Pro Tip: Both you AND your parent need separate FSA IDs at StudentAid.gov BEFORE you start!', margin + 5, y + 7);
    y += 18;

    // Step 3: Key Dates & Deadlines
    checkPageBreak(30);
    doc.setTextColor(0, 46, 98);
    doc.setFontSize(16);
    doc.text('Step 3: Key Dates & Deadlines', margin, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const deadlines = [
      'October 1: FAFSA Opens - Submit within first 2 weeks!',
      'December - April: State Deadlines - Vary by state, check yours!',
      'Varies: College Deadlines - Each school sets their own',
      'June 30: Federal Deadline - Final deadline for following year'
    ];
    deadlines.forEach(deadline => {
      checkPageBreak(6);
      doc.text('• ' + deadline, margin, y);
      y += 6;
    });
    y += 5;

    // Warning box
    checkPageBreak(12);
    doc.setFillColor(254, 243, 199); // Light yellow
    doc.roundedRect(margin, y, pageWidth - 2 * margin, 10, 2, 2, 'F');
    doc.setTextColor(161, 98, 7);
    doc.setFontSize(9);
    doc.text('Important: Students who file in the first 3 months receive TWICE as many grants on average!', margin + 5, y + 6);
    y += 18;

    // Step 4: How to Complete FAFSA
    checkPageBreak(50);
    doc.setTextColor(0, 46, 98);
    doc.setFontSize(16);
    doc.text('Step 4: How to Complete FAFSA', margin, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const fafsaSteps = [
      '1. Create Your FSA ID at StudentAid.gov (both student AND parent)',
      '2. Start Your FAFSA at fafsa.gov - log in with FSA ID',
      '3. Fill Out Student Section - personal info, school selection',
      '4. Invite Your Parent (Contributor) - they receive an email invite',
      '5. Parent Completes Their Section - tax consent, financial data',
      '6. Review and Submit - both must sign electronically',
      '7. Check Your Results - FAFSA Summary available in 1-3 days'
    ];
    fafsaSteps.forEach(step => {
      checkPageBreak(6);
      doc.text(step, margin, y);
      y += 6;
    });
    y += 10;

    // Step 5: Final Tips
    checkPageBreak(30);
    doc.setTextColor(0, 46, 98);
    doc.setFontSize(16);
    doc.text('Step 5: You\'re Ready! Final Tips', margin, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const tips = [
      '✓ File Early, File Often - Submit close to October 1, file every year',
      '✓ Keep Copies - Save your FAFSA Submission Summary',
      '✓ Compare Aid Packages - Look at grants vs loans, calculate net cost',
      '✓ Communicate with Coaches - Share FAFSA info for better packages',
      '✓ Appeal If Needed - Contact financial aid if circumstances changed'
    ];
    tips.forEach(tip => {
      checkPageBreak(6);
      doc.text(tip, margin, y);
      y += 6;
    });
    y += 10;

    // Important links
    checkPageBreak(25);
    doc.setTextColor(0, 46, 98);
    doc.setFontSize(14);
    doc.text('Important Links', margin, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const links = [
      'Start FAFSA: studentaid.gov',
      'FSA ID Creation: studentaid.gov/fsa-id/create-account',
      'State Deadlines: studentaid.gov/apply-for-aid/fafsa/fafsa-deadlines',
      'Federal Student Aid Info: studentaid.gov'
    ];
    links.forEach(link => {
      checkPageBreak(5);
      doc.text('• ' + link, margin, y);
      y += 5;
    });

    // Footer on last page
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Generated by RecruitLook - Your Recruiting Partner', margin, pageHeight - 10);
    doc.text(new Date().toLocaleDateString(), pageWidth - margin - 30, pageHeight - 10);

    // Save the PDF
    doc.save('FAFSA-Helper-Guide.pdf');
  };

  const steps = [
    { number: 1, title: 'Why Athletes MUST Fill Out FAFSA' },
    { number: 2, title: 'What You\'ll Need' },
    { number: 3, title: 'Key Dates & Deadlines' },
    { number: 4, title: 'How to Complete FAFSA' },
    { number: 5, title: 'You\'re Ready!' },
  ];

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">Even with an athletic scholarship, you need FAFSA</h3>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="bg-green-100 p-3 rounded-full">
                <Gift className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-bold text-foreground mb-2">Free Money on Top of Your Scholarship</h4>
                <p className="text-muted-foreground text-sm">
                  NCAA rules allow student-athletes to keep the ENTIRE Pell Grant even with a full athletic scholarship. 
                  That's up to <span className="font-bold text-green-600">$7,395 extra per year</span> that you don't pay back!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <GraduationCap className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-bold text-foreground mb-2">Most Athletes Don't Get Full Rides</h4>
                <p className="text-muted-foreground text-sm">
                  Only about <span className="font-bold">2% of high school athletes</span> receive athletic scholarships. 
                  Most scholarships are partial—FAFSA helps fill the gap.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="bg-purple-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-bold text-foreground mb-2">Coaches May Require It</h4>
                <p className="text-muted-foreground text-sm">
                  Many coaches and athletic departments <span className="font-bold">REQUIRE</span> all athletes to complete FAFSA. 
                  It helps them stretch scholarship budgets and build better aid packages for you.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="bg-orange-100 p-3 rounded-full">
                <Shield className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h4 className="font-bold text-foreground mb-2">Protection If Things Change</h4>
                <p className="text-muted-foreground text-sm">
                  Athletic scholarships are renewed yearly. An injury or coaching change could affect your scholarship. 
                  FAFSA-based aid provides a <span className="font-bold">safety net</span>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-pink-500 md:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="bg-pink-100 p-3 rounded-full">
                <DollarSign className="h-6 w-6 text-pink-600" />
              </div>
              <div>
                <h4 className="font-bold text-foreground mb-2">Scholarships Don't Cover Everything</h4>
                <p className="text-muted-foreground text-sm">
                  Even full scholarships don't cover student fees, parking, travel home, snacks, or personal expenses. 
                  FAFSA can help with those costs.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="text-center pt-6">
        <p className="text-muted-foreground mb-4">Ready to get started? Let's see what you'll need.</p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">Gather These Items Before You Start</h3>
        <p className="text-muted-foreground mt-2">Check off items as you collect them. Your progress is saved automatically.</p>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Checklist Progress</span>
          <span className="text-sm font-bold text-green-600">{checklistProgress}%</span>
        </div>
        <Progress value={checklistProgress} className="h-3" />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              For the Student
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {studentChecklistItems.map(item => (
              <div key={item.key} className="flex items-start gap-3">
                <Checkbox 
                  id={item.key}
                  checked={checklist[item.key] || false}
                  onCheckedChange={() => toggleChecklistItem(item.key)}
                />
                <Label 
                  htmlFor={item.key} 
                  className={cn(
                    "text-sm cursor-pointer",
                    checklist[item.key] && "line-through text-muted-foreground"
                  )}
                >
                  {item.label}
                </Label>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              For Your Parent(s)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {parentChecklistItems.map(item => (
              <div key={item.key} className="flex items-start gap-3">
                <Checkbox 
                  id={item.key}
                  checked={checklist[item.key] || false}
                  onCheckedChange={() => toggleChecklistItem(item.key)}
                />
                <Label 
                  htmlFor={item.key}
                  className={cn(
                    "text-sm cursor-pointer",
                    checklist[item.key] && "line-through text-muted-foreground"
                  )}
                >
                  {item.label}
                </Label>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-blue-800 mb-1">Pro Tip</p>
              <p className="text-blue-700 text-sm">
                Both you AND your parent need to create separate FSA IDs at{' '}
                <a href="https://studentaid.gov" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                  StudentAid.gov
                </a>{' '}
                BEFORE you start. Do this a few days early—verification can take up to 3 days.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>


      <p className="text-center text-sm text-muted-foreground">
        Worried about the parent situation?{' '}
        <button 
          onClick={() => setActiveTab('parents')} 
          className="text-primary underline hover:no-underline"
        >
          See our "Do I Need My Parents?" section
        </button>
      </p>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">Timing Matters—File Early!</h3>
      </div>

      <div className="relative">
        {/* Timeline */}
        <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-green-500 via-yellow-500 to-red-500"></div>
        
        <div className="space-y-8">
          <div className="flex items-center gap-4 md:gap-8">
            <div className="md:w-1/2 md:text-right">
              <Card className="border-green-500 border-2 inline-block">
                <CardContent className="p-4">
                  <Badge className="bg-green-500 mb-2">October 1</Badge>
                  <h4 className="font-bold text-foreground">FAFSA Opens</h4>
                  <p className="text-sm text-muted-foreground">Aim to submit within the first 2 weeks!</p>
                </CardContent>
              </Card>
            </div>
            <div className="hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-green-500 text-white font-bold z-10">
              <Calendar className="h-4 w-4" />
            </div>
            <div className="md:w-1/2"></div>
          </div>

          <div className="flex items-center gap-4 md:gap-8">
            <div className="md:w-1/2"></div>
            <div className="hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500 text-white font-bold z-10">
              <Calendar className="h-4 w-4" />
            </div>
            <div className="md:w-1/2">
              <Card className="border-yellow-500 border-2 inline-block">
                <CardContent className="p-4">
                  <Badge className="bg-yellow-500 mb-2">Dec - April</Badge>
                  <h4 className="font-bold text-foreground">State Deadlines</h4>
                  <p className="text-sm text-muted-foreground">Vary by state—check yours!</p>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex items-center gap-4 md:gap-8">
            <div className="md:w-1/2 md:text-right">
              <Card className="border-orange-500 border-2 inline-block">
                <CardContent className="p-4">
                  <Badge className="bg-orange-500 mb-2">Varies</Badge>
                  <h4 className="font-bold text-foreground">College Deadlines</h4>
                  <p className="text-sm text-muted-foreground">Each school sets their own—check with every school</p>
                </CardContent>
              </Card>
            </div>
            <div className="hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white font-bold z-10">
              <Calendar className="h-4 w-4" />
            </div>
            <div className="md:w-1/2"></div>
          </div>

          <div className="flex items-center gap-4 md:gap-8">
            <div className="md:w-1/2"></div>
            <div className="hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-red-500 text-white font-bold z-10">
              <Calendar className="h-4 w-4" />
            </div>
            <div className="md:w-1/2">
              <Card className="border-red-500 border-2 inline-block">
                <CardContent className="p-4">
                  <Badge className="bg-red-500 mb-2">June 30</Badge>
                  <h4 className="font-bold text-foreground">Federal Deadline</h4>
                  <p className="text-sm text-muted-foreground">Final deadline for the following year</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Card className="bg-yellow-50 border-yellow-300 mt-8">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-yellow-800 mb-1">Important!</p>
              <p className="text-yellow-700 text-sm">
                Many states and schools award aid on a <span className="font-bold">FIRST-COME, FIRST-SERVED</span> basis. 
                Students who file in the first 3 months receive <span className="font-bold">TWICE as many grants</span> on average!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-4 justify-center pt-4">
        <Button variant="outline" asChild>
          <a href="https://studentaid.gov/apply-for-aid/fafsa/fafsa-deadlines" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4 mr-2" />
            Check State Deadlines
          </a>
        </Button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">It's Easier Than You Think</h3>
        <p className="text-muted-foreground">Most people finish in about 30 minutes</p>
      </div>

      <div className="space-y-4">
        {[
          {
            num: 1,
            title: 'Create Your FSA ID',
            content: (
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Go to <a href="https://studentaid.gov" target="_blank" rel="noopener noreferrer" className="text-primary underline">StudentAid.gov</a></li>
                <li>Both student AND parent create separate accounts</li>
                <li>You'll need your Social Security Number and email</li>
                <li>Wait 1-3 days for verification (or instant if SSN verified)</li>
              </ul>
            )
          },
          {
            num: 2,
            title: 'Start Your FAFSA',
            content: (
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Go to <a href="https://fafsa.gov" target="_blank" rel="noopener noreferrer" className="text-primary underline">fafsa.gov</a></li>
                <li>Log in with your FSA ID</li>
                <li>Select the correct school year</li>
              </ul>
            )
          },
          {
            num: 3,
            title: 'Fill Out Student Section',
            content: (
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Personal info, school selection, dependency questions</li>
                <li>Consent to have IRS transfer your tax info automatically</li>
              </ul>
            )
          },
          {
            num: 4,
            title: 'Invite Your Parent (Contributor)',
            content: (
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Enter parent's email address</li>
                <li>They'll receive an invite to complete their section</li>
                <li>They must log in with THEIR FSA ID</li>
              </ul>
            )
          },
          {
            num: 5,
            title: 'Parent Completes Their Section',
            content: (
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Similar process—personal info, tax consent, financial data</li>
                <li>Parent signs electronically</li>
              </ul>
            )
          },
          {
            num: 6,
            title: 'Review and Submit',
            content: (
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Both student and parent must sign</li>
                <li>Review for errors before submitting</li>
                <li>You'll see a confirmation on screen</li>
              </ul>
            )
          },
          {
            num: 7,
            title: 'Check Your Results',
            content: (
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>FAFSA Submission Summary available in 1-3 days</li>
                <li>Schools will send financial aid award letters</li>
                <li>Review and compare offers!</li>
              </ul>
            )
          },
        ].map((step) => (
          <Card key={step.num} className="overflow-hidden">
            <CardContent className="p-0">
              <div className="flex">
                <div className="bg-primary text-primary-foreground w-12 flex items-center justify-center font-bold text-lg shrink-0">
                  {step.num}
                </div>
                <div className="p-4 flex-1">
                  <h4 className="font-bold text-foreground mb-2">{step.title}</h4>
                  {step.content}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-blue-800 mb-1">Need Help?</p>
              <p className="text-blue-700 text-sm">
                Use the help icons on each question, or chat with Aidan (the FAFSA virtual assistant) while filling out the form.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">Go Get That Money!</h3>
        <p className="text-muted-foreground">You're ready to complete your FAFSA</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardContent className="p-5">
            <Clock className="h-8 w-8 text-primary mb-3" />
            <h4 className="font-bold text-foreground mb-2">File Early, File Often</h4>
            <p className="text-sm text-muted-foreground">
              Submit as close to October 1 as possible. You must file FAFSA every year you're in college.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <FileText className="h-8 w-8 text-primary mb-3" />
            <h4 className="font-bold text-foreground mb-2">Keep Copies</h4>
            <p className="text-sm text-muted-foreground">
              Save your FAFSA Submission Summary. Keep your documents in a safe place for future years.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <Calculator className="h-8 w-8 text-primary mb-3" />
            <h4 className="font-bold text-foreground mb-2">Compare Aid Packages</h4>
            <p className="text-sm text-muted-foreground">
              Don't just look at the total—compare grants vs loans. Net cost = Total Cost - Grants/Scholarships.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <Users className="h-8 w-8 text-primary mb-3" />
            <h4 className="font-bold text-foreground mb-2">Communicate with Coaches</h4>
            <p className="text-sm text-muted-foreground">
              Share your FAFSA info with coaches recruiting you. It helps them build better scholarship packages.
            </p>
          </CardContent>
        </Card>

        <Card className="md:col-span-2 lg:col-span-2">
          <CardContent className="p-5">
            <Heart className="h-8 w-8 text-primary mb-3" />
            <h4 className="font-bold text-foreground mb-2">Appeal If Needed</h4>
            <p className="text-sm text-muted-foreground">
              If family finances changed, contact the school's financial aid office. They can adjust based on special circumstances.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="text-center pt-6">
        <Button size="lg" className="bg-green-600 hover:bg-green-700" asChild>
          <a href="https://studentaid.gov" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-5 w-5 mr-2" />
            Start Your FAFSA Now
          </a>
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 justify-center pt-4">
        <Button variant="outline" size="sm" asChild>
          <a href="https://studentaid.gov/fsa-id/create-account" target="_blank" rel="noopener noreferrer">
            FSA ID Creation
          </a>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <a href="https://studentaid.gov" target="_blank" rel="noopener noreferrer">
            Federal Student Aid Info
          </a>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <a href="https://studentaid.gov/apply-for-aid/fafsa/filling-out" target="_blank" rel="noopener noreferrer">
            FAFSA Help
          </a>
        </Button>
      </div>
    </div>
  );

  const renderGetStarted = () => (
    <div className="space-y-8">
      {/* Stepper Navigation */}
      <div className="flex items-center justify-between overflow-x-auto pb-4">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <button
              onClick={() => setCurrentStep(step.number)}
              className={cn(
                "flex flex-col items-center min-w-[80px] transition-colors",
                currentStep === step.number ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center font-bold mb-2 transition-colors",
                currentStep === step.number 
                  ? "bg-primary text-primary-foreground" 
                  : currentStep > step.number 
                    ? "bg-green-500 text-white"
                    : "bg-muted text-muted-foreground"
              )}>
                {currentStep > step.number ? <Check className="h-5 w-5" /> : step.number}
              </div>
              <span className="text-xs text-center hidden sm:block max-w-[100px]">{step.title}</span>
            </button>
            {index < steps.length - 1 && (
              <div className={cn(
                "h-1 flex-1 mx-2 rounded",
                currentStep > step.number ? "bg-green-500" : "bg-muted"
              )} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
          disabled={currentStep === 1}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        <Button 
          onClick={() => setCurrentStep(prev => Math.min(5, prev + 1))}
          disabled={currentStep === 5}
        >
          Next Step
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderPellGrants = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">Pell Grants: Free Money You Don't Pay Back</h3>
        <p className="text-muted-foreground mt-2">
          A Pell Grant is federal money given to students based on financial need. Unlike loans, you NEVER have to pay it back. 
          It's essentially a gift from the government to help you afford college.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5 text-green-600" />
              How Much Can You Get?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                Up to <span className="font-bold text-foreground">$7,395 per year</span> (2024-25 school year)
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                Amount depends on your financial need, cost of attendance, and enrollment status
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                Full-time students receive more than part-time students
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-blue-600" />
              Who Qualifies?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                Students with family income generally under $60,000/year (but apply anyway!)
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                Must be an undergraduate who hasn't earned a bachelor's degree
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                Must be a U.S. citizen or eligible non-citizen
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-green-300 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-green-800">
              <Award className="h-5 w-5 text-green-600" />
              Athletes Get to Keep It All
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-green-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                NCAA rules allow you to receive your FULL Pell Grant on top of your athletic scholarship
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                Even athletes on full-ride scholarships can get Pell Grants
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                This is extra cash in your pocket for expenses scholarships don't cover
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-purple-600" />
              It's Renewable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                You can receive Pell Grants for up to 6 years (12 semesters)
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                Must re-file FAFSA every year to continue receiving it
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Gift className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
            <p className="text-green-700">
              Think of Pell Grants as a <span className="font-bold">reward for filling out FAFSA</span>. 
              It takes 30 minutes to complete and could put thousands of dollars in your pocket every year.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderCalculator = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">Estimate Your Pell Grant</h3>
        <p className="text-muted-foreground mt-2">
          This is a rough estimate based on family income. Your actual amount depends on many factors calculated from your FAFSA.
        </p>
      </div>

      <Card className="max-w-xl mx-auto">
        <CardContent className="p-6 space-y-6">
          <div>
            <Label htmlFor="household">Household Size</Label>
            <Select value={householdSize} onValueChange={setHouseholdSize}>
              <SelectTrigger id="household" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2, 3, 4, 5, 6, 7, '8+'].map(size => (
                  <SelectItem key={size} value={String(size)}>{size} people</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="income">Total Family Income</Label>
            <Select value={familyIncome} onValueChange={setFamilyIncome}>
              <SelectTrigger id="income" className="mt-2">
                <SelectValue placeholder="Select income range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="under20">Less than $20,000</SelectItem>
                <SelectItem value="20-30">$20,000 - $30,000</SelectItem>
                <SelectItem value="30-40">$30,000 - $40,000</SelectItem>
                <SelectItem value="40-50">$40,000 - $50,000</SelectItem>
                <SelectItem value="50-60">$50,000 - $60,000</SelectItem>
                <SelectItem value="60-70">$60,000 - $70,000</SelectItem>
                <SelectItem value="over70">More than $70,000</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="enrollment">Enrollment Status</Label>
            <Select value={enrollmentStatus} onValueChange={setEnrollmentStatus}>
              <SelectTrigger id="enrollment" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full-time">Full-time</SelectItem>
                <SelectItem value="three-quarter">Three-quarter time</SelectItem>
                <SelectItem value="half-time">Half-time</SelectItem>
                <SelectItem value="less-than-half">Less than half-time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={calculatePellGrant} className="w-full" disabled={!familyIncome}>
            <Calculator className="h-4 w-4 mr-2" />
            Calculate Estimate
          </Button>

          {calculatorResult && (
            <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200 text-center">
              {calculatorResult.max === 0 ? (
                <div>
                  <p className="text-lg font-bold text-orange-600">Likely $0 based on income</p>
                  <p className="text-sm text-muted-foreground mt-2">But apply anyway! Cutoffs vary and your situation may qualify.</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground">Estimated Pell Grant:</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${calculatorResult.min.toLocaleString()} - ${calculatorResult.max.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">per year</p>
                  <p className="text-lg font-semibold text-primary mt-4">
                    Over 4 years: ${(calculatorResult.min * 4).toLocaleString()} - ${(calculatorResult.max * 4).toLocaleString()} in FREE money!
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200 max-w-xl mx-auto">
        <CardContent className="p-5">
          <p className="text-blue-700 text-sm">
            <span className="font-bold">Disclaimer:</span> This is just an estimate! Your actual Pell Grant is calculated using the full FAFSA formula. 
            The only way to know for sure is to complete your FAFSA.
          </p>
        </CardContent>
      </Card>

      <div className="text-center">
        <Button variant="outline" asChild>
          <a href="https://studentaid.gov/aid-estimator/" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4 mr-2" />
            Get Your Real Estimate
          </a>
        </Button>
      </div>
    </div>
  );

  const renderGrantsVsLoans = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">Know the Difference: Free Money vs. Borrowed Money</h3>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Grants & Scholarships */}
        <Card className="border-green-300">
          <CardHeader className="bg-green-50">
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Gift className="h-5 w-5" />
              Grants & Scholarships
            </CardTitle>
            <p className="text-sm text-green-600 font-medium">The Good Stuff</p>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <p className="font-bold text-foreground">Free money you don't have to pay back. Ever.</p>
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-2">Types:</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• <span className="font-medium">Pell Grants</span> - Federal, need-based, up to $7,395/year</li>
                <li>• <span className="font-medium">FSEOG Grants</span> - Federal, exceptional need, $100-$4,000/year</li>
                <li>• <span className="font-medium">State Grants</span> - Vary by state</li>
                <li>• <span className="font-medium">Institutional Grants</span> - From your college</li>
                <li>• <span className="font-medium">Athletic Scholarships</span> - From athletic dept</li>
                <li>• <span className="font-medium">Academic Scholarships</span> - Based on merit</li>
                <li>• <span className="font-medium">Private Scholarships</span> - From organizations</li>
              </ul>
            </div>
            <div className="pt-2 border-t">
              <p className="text-sm font-bold text-green-700">
                ✓ Always maximize grants and scholarships FIRST. This is money you keep.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Federal Loans */}
        <Card className="border-blue-300">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Building className="h-5 w-5" />
              Federal Student Loans
            </CardTitle>
            <p className="text-sm text-blue-600 font-medium">Borrow Carefully</p>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <p className="font-bold text-foreground">Money you borrow from the government and MUST pay back with interest.</p>
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-2">Types:</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• <span className="font-medium">Direct Subsidized</span> - Govt pays interest while in school</li>
                <li>• <span className="font-medium">Direct Unsubsidized</span> - Interest accrues immediately</li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-2">Why Better Than Private:</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>✓ Fixed interest rates</li>
                <li>✓ Income-driven repayment plans</li>
                <li>✓ Loan forgiveness programs</li>
                <li>✓ Deferment/forbearance options</li>
                <li>✓ No credit check for most</li>
                <li>✓ Grace period after graduation</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Private Loans */}
        <Card className="border-orange-300">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="h-5 w-5" />
              Private Loans
            </CardTitle>
            <p className="text-sm text-orange-600 font-medium">Last Resort</p>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <p className="font-bold text-foreground">Money borrowed from banks or other lenders. These should be your LAST option.</p>
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-2">Why to Avoid:</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>✗ Variable interest rates</li>
                <li>✗ Often require co-signer</li>
                <li>✗ Fewer repayment options</li>
                <li>✗ No loan forgiveness</li>
                <li>✗ Less flexibility</li>
                <li>✗ Higher interest rates</li>
                <li>✗ Credit check required</li>
              </ul>
            </div>
            <div className="pt-2 border-t">
              <p className="text-sm text-orange-700">
                Only consider if you've maxed out federal aid.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6">
          <h4 className="font-bold text-foreground mb-4">The Smart Order of Operations:</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="font-medium">Grants & Scholarships</span>
              <span className="text-sm text-muted-foreground">— free money, always first!</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-blue-500" />
              <span className="font-medium">Federal Subsidized Loans</span>
              <span className="text-sm text-muted-foreground">— if needed, interest paid while in school</span>
            </div>
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <span className="font-medium">Federal Unsubsidized Loans</span>
              <span className="text-sm text-muted-foreground">— if needed, interest accrues</span>
            </div>
            <div className="flex items-center gap-3">
              <X className="h-5 w-5 text-red-500" />
              <span className="font-medium">Private Loans</span>
              <span className="text-sm text-muted-foreground">— only as a last resort</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <p className="text-blue-700 text-sm">
              <span className="font-bold">Pro Tip:</span> Your financial aid award letter will show a mix of grants, scholarships, and loans. 
              You do NOT have to accept the loans! Only borrow what you truly need.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderParentsSection = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">Filling Out FAFSA When Family Is Complicated</h3>
        <p className="text-muted-foreground mt-2">
          We know not everyone has a straightforward family situation. You're not alone, and there ARE options. 
          Don't let this stop you from getting financial aid.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Understanding "Dependency Status"</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <p className="font-bold text-foreground mb-2">The Default Rule:</p>
            <p className="text-muted-foreground text-sm">
              For FAFSA purposes, most students under 24 are considered "dependent" and need to include parent information—even if 
              you don't live with them, they don't support you financially, or you file your own taxes.
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            <span className="font-bold">Why?</span> The government assumes parents SHOULD help pay for college, even if they don't or won't. 
            It's frustrating, but there are exceptions.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-green-700">You Might Be "Independent" If...</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">Answer YES to any of these on FAFSA:</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              "You're 24 years old or older by December 31 of the school year",
              "You're married (or separated but not divorced)",
              "You're working on a master's or doctoral degree",
              "You're currently serving on active duty in the U.S. military",
              "You're a veteran of the U.S. Armed Forces",
              "You have children who receive more than half their support from you",
              "You have dependents (other than children/spouse) who live with you",
              "You were in foster care at any time since turning 13",
              "You were a dependent or ward of the court since turning 13",
              "You're an emancipated minor or in legal guardianship",
              "You're an unaccompanied homeless youth",
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm font-medium text-green-700">
            If any of these apply, you do NOT need parent information on FAFSA.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What If None of Those Apply But You Still Can't Get Parent Info?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-bold text-foreground mb-2">"Unusual Circumstances" Exception:</h4>
            <p className="text-sm text-muted-foreground mb-3">
              The FAFSA allows for exceptions if you have an "unusual circumstance" that prevents you from providing parent information:
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Your parents are incarcerated</li>
              <li>• You've left home due to an abusive situation</li>
              <li>• You're estranged from your parents and have no contact</li>
              <li>• You don't know where your parents are</li>
              <li>• Your parents refuse to provide their information</li>
              <li>• You're at risk of harm if you contact your parents</li>
            </ul>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-bold text-blue-800 mb-2">How to Request This:</h4>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>1. On the FAFSA: Indicate that you have an unusual circumstance when asked</li>
              <li>2. Contact the financial aid office at each school you're applying to</li>
              <li>3. Provide documentation if possible (letters from counselors, teachers, etc.)</li>
              <li>4. Request a "Dependency Override" - The financial aid office can review your case</li>
            </ul>
            <p className="mt-3 text-sm text-blue-600 font-medium">
              Important: Each school makes this decision independently.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Specific Situations</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="divorced">
              <AccordionTrigger>Parents Are Divorced</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Include information for the parent you lived with MORE during the past 12 months</li>
                  <li>• If equal time, use the parent who provided more financial support</li>
                  <li>• If that parent is remarried, you must also include stepparent information</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="refuse">
              <AccordionTrigger>Parents Refuse to Help or Provide Info</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• You can still submit FAFSA without parent info, but it will be incomplete</li>
                  <li>• Contact the financial aid office immediately to explain</li>
                  <li>• They may be able to grant a dependency override or help you find other options</li>
                  <li>• You may still qualify for unsubsidized federal loans</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="locate">
              <AccordionTrigger>Can't Locate a Parent</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Document your attempts to find them</li>
                  <li>• Contact the financial aid office to explain</li>
                  <li>• They can guide you through the process</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="undocumented">
              <AccordionTrigger>Undocumented Parents</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Parents without Social Security numbers CAN still complete FAFSA</li>
                  <li>• They create an account and enter all zeros for SSN</li>
                  <li>• They are not asked about citizenship status</li>
                  <li>• Their information is protected and not shared with immigration</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Resources for Help</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Talk to Someone:</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Your high school counselor</li>
              <li>• A trusted teacher or coach</li>
              <li>• The financial aid office at schools you're interested in</li>
              <li>• A social worker (if applicable)</li>
            </ul>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href="https://studentaid.gov/apply-for-aid/fafsa/filling-out" target="_blank" rel="noopener noreferrer">
                FAFSA Help
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href="https://studentaid.gov/apply-for-aid/fafsa/filling-out/dependency" target="_blank" rel="noopener noreferrer">
                Dependency Status Info
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href="https://studentaid.gov/apply-for-aid/fafsa/filling-out/parent-info" target="_blank" rel="noopener noreferrer">
                "Who's My Parent?" Wizard
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Heart className="h-5 w-5 text-purple-600 mt-0.5 shrink-0" />
            <p className="text-purple-700">
              Your family situation should NOT prevent you from getting an education. Financial aid offices deal with complicated 
              situations every day. Reach out, explain your circumstances, and ask for help. <span className="font-bold">There is almost always a path forward.</span>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const faqItems = [
    {
      q: "Do I have to fill out FAFSA if I'm getting an athletic scholarship?",
      a: "You should! Many coaches and schools REQUIRE it. Plus, you can receive Pell Grants ON TOP of your athletic scholarship—that's extra money in your pocket. Even full-ride athletes can qualify for thousands in additional aid."
    },
    {
      q: "How long does it take to fill out FAFSA?",
      a: "Most people complete it in about 30 minutes. The IRS now transfers your tax information directly, so you don't have to enter it manually."
    },
    {
      q: "Is there a fee to submit FAFSA?",
      a: "No! FAFSA is completely FREE. If a website asks you to pay, you're not on the official site. Always use studentaid.gov."
    },
    {
      q: "When should I submit my FAFSA?",
      a: "As early as possible after October 1. Many schools and states award aid on a first-come, first-served basis. Students who file early get twice as many grants on average."
    },
    {
      q: "Do I have to file FAFSA every year?",
      a: "Yes. You must submit a new FAFSA each year you want to receive federal financial aid."
    },
    {
      q: "What if my family's financial situation changed after filing taxes?",
      a: "Complete FAFSA using the required tax year, then contact the financial aid office at your school. They can make adjustments based on \"special circumstances\" like job loss, medical expenses, or other changes."
    },
    {
      q: "Can I file FAFSA if my parents are undocumented?",
      a: "Yes! Parents without Social Security numbers can still complete their portion of the FAFSA. They create an account, enter zeros for SSN, and are not asked about citizenship status."
    },
    {
      q: "What if my parents won't help me fill out FAFSA?",
      a: "See our \"Do I Need My Parents?\" section. You can submit without parent info (though it will be incomplete), and you should contact the financial aid office to discuss your options, including a possible dependency override."
    },
    {
      q: "What's the difference between FAFSA and CSS Profile?",
      a: "FAFSA is the federal application—required by all schools for federal aid. CSS Profile is a separate form required by some (mostly private) schools to award their own institutional aid. Check each school's requirements."
    },
    {
      q: "Do I need to list schools on my FAFSA?",
      a: "Yes. List every school you're considering—you can add up to 20 schools. They'll each receive your information to create financial aid packages."
    },
    {
      q: "What if I made a mistake on my FAFSA?",
      a: "You can make corrections after submitting. Log back into studentaid.gov to update your information. Corrections must be submitted by the deadline (September 14 of the following year for most situations)."
    },
    {
      q: "Will my FAFSA information affect my athletic scholarship offer?",
      a: "It can actually help! Coaches use your Expected Family Contribution (EFC) to build better scholarship packages. If you qualify for need-based aid, they may be able to combine it with athletic aid to give you a better overall package."
    },
    {
      q: "What's a Student Aid Index (SAI)?",
      a: "The SAI (formerly called Expected Family Contribution or EFC) is a number calculated from your FAFSA data. Schools use it to determine how much financial aid you're eligible for. A lower SAI generally means more aid."
    },
    {
      q: "Can I receive both Pell Grants and athletic scholarships?",
      a: "Absolutely! NCAA rules specifically allow student-athletes to receive Pell Grants on top of their athletic scholarships. This is one of the best reasons for athletes to complete FAFSA."
    },
    {
      q: "What happens after I submit FAFSA?",
      a: "Within 1-3 days, you'll receive a FAFSA Submission Summary in your studentaid.gov account. Then, each school you listed will send you a financial aid award letter showing what aid you qualify for at that school."
    },
  ];

  const renderFAQs = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground">Frequently Asked Questions</h3>
      </div>

      <Card>
        <CardContent className="p-6">
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`faq-${index}`}>
                <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-8 pb-12">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-8 text-white">
        <div className="max-w-3xl">
          <div className="flex items-start justify-between mb-2">
            <h1 className="text-3xl md:text-4xl font-bold">FAFSA Helper</h1>
            <Button 
              onClick={generatePDF}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Guide
            </Button>
          </div>
          <p className="text-lg md:text-xl opacity-90 mb-6">
            Your step-by-step guide to unlocking FREE money for college
          </p>
          <Card className="bg-white/10 border-white/20">
            <CardContent className="p-4">
              <p className="text-white text-center">
                <span className="text-2xl font-bold">$3.58 BILLION</span> in Pell Grant money goes unclaimed every year.
                <br />
                <span className="font-medium">Don't leave money on the table!</span>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto pb-2">
          <TabsList className="inline-flex w-auto min-w-full md:w-full justify-start md:justify-center gap-1">
            <TabsTrigger value="get-started" className="whitespace-nowrap">Get Started</TabsTrigger>
            <TabsTrigger value="pell-grants" className="whitespace-nowrap">Pell Grants</TabsTrigger>
            <TabsTrigger value="calculator" className="whitespace-nowrap">Calculator</TabsTrigger>
            <TabsTrigger value="grants-loans" className="whitespace-nowrap">Grants vs Loans</TabsTrigger>
            <TabsTrigger value="parents" className="whitespace-nowrap">Parents?</TabsTrigger>
            <TabsTrigger value="faqs" className="whitespace-nowrap">FAQs</TabsTrigger>
          </TabsList>
        </div>

        <div className="mt-6">
          <TabsContent value="get-started">{renderGetStarted()}</TabsContent>
          <TabsContent value="pell-grants">{renderPellGrants()}</TabsContent>
          <TabsContent value="calculator">{renderCalculator()}</TabsContent>
          <TabsContent value="grants-loans">{renderGrantsVsLoans()}</TabsContent>
          <TabsContent value="parents">{renderParentsSection()}</TabsContent>
          <TabsContent value="faqs">{renderFAQs()}</TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default FafsaHelper;
